import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, Link as RouterLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Page from "../Page";
import { BaseTable } from "../BaseTable/BaseTable";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import {
  toList,
  getFleetOperatorId,
  statusChipColor,
} from "./fleet.constants";

const SummaryCard = ({ title, value }) => (
  <Card sx={{ p: 2 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h4">{value}</Typography>
  </Card>
);

const FleetDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const isReadOnly = useAccess(FEATURE.FLEET_MANAGEMENT, "R");
  const { data, isFetching } = useList("fleetOperators");
  const operators = useMemo(() => toList(data), [data]);
  const [search, setSearch] = useState("");

  const isFleetEnabled =
    user?.company?.enabledFeatures?.includes(FEATURE.FLEET_MANAGEMENT) ?? false;
  const fleetOperatorScope = user?.company?.fleetOperatorScope;

  useEffect(() => {
    setSearch("");
  }, [isFetching]);

  const tableData = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) return operators;
    return operators.filter((operator) =>
      ["name", "legal_name", "fleet_type", "status", "contact_email"]
        .map((field) => (operator?.[field] ?? "").toString().toLowerCase())
        .some((value) => value.includes(searchValue))
    );
  }, [operators, search]);

  const activeCount = useMemo(
    () =>
      operators.filter(
        (operator) => (operator?.status ?? "").toLowerCase() === "active"
      ).length,
    [operators]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        renderCell: ({ row, value }) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/fleet/operators/${getFleetOperatorId(row)}`}
          >
            {value ?? "-"}
          </Link>
        ),
      },
      {
        field: "fleet_type",
        headerName: "Fleet Type",
        flex: 1,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ?? "-"}
            color={statusChipColor(value)}
          />
        ),
      },
      {
        field: "contact_email",
        headerName: "Contact Email",
        flex: 1,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row">
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/fleet/operators/${getFleetOperatorId(row)}`)
                }
              >
                <Icon icon="eva:eye-outline" />
              </IconButton>
            </Tooltip>
            {hasUpdateAccess && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/fleet/operators/${getFleetOperatorId(row)}/edit`)
                  }
                >
                  <Icon icon="eva:edit-outline" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [navigate, hasUpdateAccess]
  );

  if (!isFleetEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  // Scoped fleet-operator logins can only see their own operator — land them there.
  if (fleetOperatorScope != null) {
    return <Navigate to={`/fleet/operators/${fleetOperatorScope}`} replace />;
  }

  return (
    <Page
      title="Fleet | Smart-CMS"
      actions={
        hasCreateAccess
          ? [{ text: "Add Fleet Operator", path: "/fleet/operators/new" }]
          : []
      }
    >
      <Typography variant="h4" gutterBottom>
        Fleet Operators
      </Typography>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Operators"
            value={isFetching ? "-" : operators.length}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active Operators"
            value={isFetching ? "-" : activeCount}
          />
        </Grid>
      </Grid>
      <Card sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <CommonSearchbar
            placeholder="Search"
            searchFunc={(e) => setSearch(e.target.value)}
          />
        </Stack>
        <BaseTable
          rows={tableData ?? []}
          columns={columns}
          loading={isFetching}
          getRowId={(row) => getFleetOperatorId(row)}
          rowOrCellClickRedirect={({ id }) => navigate(`/fleet/operators/${id}`)}
          pageSize={10}
          isReadOnly={isReadOnly}
        />
        {!isFetching && !operators.length && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 2 }}
          >
            No fleet operators yet.
            {hasCreateAccess &&
              ' Use "Add Fleet Operator" to onboard your first fleet.'}
          </Typography>
        )}
      </Card>
    </Page>
  );
};

export default FleetDashboard;
